/**
 * Appointment Details Page - View and manage single appointment
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppointment } from '@/contexts/AppointmentContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { ArrowLeft, Calendar, Clock, User, MapPin, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { toast } from '@/lib/toast';
import { Appointment } from '@/types';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export default function AppointmentDetailsPage() {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const { getAppointment, cancelAppointment, confirmAppointment, completeAppointment } = useAppointment();
  const { user } = useAuth();

  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadAppointment();
  }, [appointmentId]);

  const loadAppointment = async () => {
    if (!appointmentId) {
      navigate('/appointments');
      return;
    }

    setLoading(true);
    try {
      const data = await getAppointment(appointmentId);
      if (!data) {
        toast.error('Cita no encontrada');
        navigate('/appointments');
        return;
      }
      setAppointment(data);
    } catch (error) {
      console.error('Error loading appointment:', error);
      toast.error('Error al cargar la cita');
      navigate('/appointments');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!appointmentId) return;

    setActionLoading(true);
    try {
      await cancelAppointment(appointmentId);
      toast.success('Cita cancelada exitosamente');
      await loadAppointment();
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast.error('Error al cancelar la cita');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!appointmentId) return;

    setActionLoading(true);
    try {
      await confirmAppointment(appointmentId);
      toast.success('Cita confirmada exitosamente');
      await loadAppointment();
    } catch (error) {
      console.error('Error confirming appointment:', error);
      toast.error('Error al confirmar la cita');
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!appointmentId) return;

    setActionLoading(true);
    try {
      await completeAppointment(appointmentId);
      toast.success('Cita completada exitosamente');
      await loadAppointment();
    } catch (error) {
      console.error('Error completing appointment:', error);
      toast.error('Error al completar la cita');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-accent/10 text-accent border-accent/30 text-accent border-accent/30';
      case 'confirmed':
        return 'bg-info/10 text-info border-info/30 text-info border-info/30';
      case 'in_progress':
        return 'bg-accent/10 text-accent border-accent/30 text-accent border-accent/30';
      case 'completed':
        return 'bg-success/10 text-success border-success/30 text-success border-success/30';
      case 'cancelled':
        return 'bg-danger/10 text-danger border-danger/30 text-danger border-danger/30';
      case 'no_show':
        return 'bg-surface-2 text-ink border-hairline bg-surface-2 text-ink-muted border-hairline';
      default:
        return 'bg-surface-2 text-ink border-hairline bg-surface-2 text-ink-muted border-hairline';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5" />;
      case 'cancelled':
      case 'no_show':
        return <XCircle className="w-5 h-5" />;
      default:
        return <AlertCircle className="w-5 h-5" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pendiente de confirmación';
      case 'confirmed':
        return 'Confirmada';
      case 'in_progress':
        return 'En progreso';
      case 'completed':
        return 'Completada';
      case 'cancelled':
        return 'Cancelada';
      case 'no_show':
        return 'No asistió';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-info/30 border-r-transparent"></div>
          <p className="mt-4 text-ink-muted">Cargando cita...</p>
        </div>
      </div>
    );
  }

  if (!appointment) {
    return null;
  }

  const canCancel = appointment.status === 'pending' || appointment.status === 'confirmed';
  const canConfirm = appointment.status === 'pending' && user?.role === 'barber';
  const canComplete = appointment.status === 'confirmed' && user?.role === 'barber';

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/appointments')}
          className="flex items-center gap-2 text-ink-muted hover:text-ink dark:hover:text-white mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver a citas
        </button>
        <h1 className="text-3xl font-bold text-ink">Detalles de la Cita</h1>
      </div>

      {/* Status Banner */}
      <div className={`border-2 rounded-lg p-4 mb-6 flex items-center gap-3 ${getStatusColor(appointment.status)}`}>
        {getStatusIcon(appointment.status)}
        <div>
          <p className="font-semibold">{getStatusText(appointment.status)}</p>
          <p className="text-sm opacity-90">
            Creada el {format(appointment.createdAt.toDate(), "d 'de' MMMM yyyy 'a las' HH:mm", { locale: es })}
          </p>
        </div>
      </div>

      {/* Main Info */}
      <div className="bg-surface rounded-lg shadow-sm p-6 mb-6">
        <h2 className="text-xl font-semibold text-ink mb-6">
          Información de la Cita
        </h2>

        <div className="space-y-6">
          {/* Service */}
          <div className="flex items-start gap-4 pb-6 border-b border-hairline">
            <div className="bg-info/10 p-3 rounded-lg">
              <Calendar className="w-6 h-6 text-info" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-ink-subtle">Servicio</p>
              <p className="text-lg font-semibold text-ink">{appointment.serviceName}</p>
              <p className="text-sm text-ink-muted mt-1">
                Duración: {appointment.serviceDuration} minutos
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-ink-subtle">Precio</p>
              <p className="text-2xl font-bold text-ink">
                €{(appointment.servicePrice / 100).toFixed(2)}
              </p>
            </div>
          </div>

          {/* Date & Time */}
          <div className="flex items-start gap-4 pb-6 border-b border-hairline">
            <div className="bg-accent/10 p-3 rounded-lg">
              <Clock className="w-6 h-6 text-accent" />
            </div>
            <div>
              <p className="text-sm text-ink-subtle">Fecha y hora</p>
              <p className="text-lg font-semibold text-ink">
                {format(appointment.dateTime.toDate(), "EEEE, d 'de' MMMM yyyy", { locale: es })}
              </p>
              <p className="text-lg font-semibold text-ink mt-1">
                {format(appointment.dateTime.toDate(), 'HH:mm')} hrs
              </p>
            </div>
          </div>

          {/* Client/Barber Info */}
          <div className="flex items-start gap-4 pb-6 border-b border-hairline">
            <div className="bg-success/10 p-3 rounded-lg">
              <User className="w-6 h-6 text-success" />
            </div>
            <div className="flex-1">
              <div className="mb-4">
                <p className="text-sm text-ink-subtle">Cliente</p>
                <p className="text-lg font-semibold text-ink">{appointment.userName}</p>
                <p className="text-sm text-ink-muted">{appointment.userEmail}</p>
                {appointment.userPhone && (
                  <p className="text-sm text-ink-muted">{appointment.userPhone}</p>
                )}
              </div>
              <div>
                <p className="text-sm text-ink-subtle">Barbero</p>
                <p className="text-lg font-semibold text-ink">{appointment.barberName}</p>
              </div>
            </div>
          </div>

          {/* Notes */}
          {appointment.notes && (
            <div className="flex items-start gap-4">
              <div className="bg-warning/10 p-3 rounded-lg">
                <MapPin className="w-6 h-6 text-warning" />
              </div>
              <div>
                <p className="text-sm text-ink-subtle">Notas</p>
                <p className="text-ink">{appointment.notes}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      {(canCancel || canConfirm || canComplete) && (
        <div className="bg-surface rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-ink mb-4">Acciones</h3>
          <div className="flex flex-wrap gap-3">
            {canConfirm && (
              <Button
                onClick={handleConfirm}
                disabled={actionLoading}
                className="flex items-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                Confirmar Cita
              </Button>
            )}

            {canComplete && (
              <Button
                onClick={handleComplete}
                disabled={actionLoading}
                className="flex items-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                Completar Cita
              </Button>
            )}

            {canCancel && (
              <Button
                onClick={handleCancel}
                disabled={actionLoading}
                variant="outline"
                className="flex items-center gap-2 text-danger hover:text-danger"
              >
                <XCircle className="w-5 h-5" />
                Cancelar Cita
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
